export const enum RouteEntities {
    Submit = "submit",
    FlowRedirector = "flow-redirector",
    LensStudio = "lens-studio",
    LensFolders = "lens-folders",
    Lenses = "lenses",
    // backwards compatibility
    Lens = "lens",
    Eligibility = "eligibility",
    Accounts = "accounts",
    Insights = "insights",
    Audience = "audience",
    Engagement = "engagement",
    EventInsights = "event-insights",
    Conversion = "conversion",
    Funnel = "funnel",
    Monetization = "monetization",
    Welcome = "welcome",
    CommunityAudience = "community-audience",
    Apis = "apis",
    Assets = "assets",
    Organization = "organization",
    Home = "home",

    HostedLenses = "hosted-lenses",
    WebLenses = "web-lenses",
    RemoteAssets = "remote-assets",

    // Camera Kit
    CameraKit = "camera-kit",
    LensScheduler = "lens-scheduler",

    References = "references",
    Sources = "sources",
    Groups = "groups",
    Apps = "apps",
    Eula = "eula",
}

export const enum RouteActions {
    Select = "select",
    Create = "create",
    Update = "update",
    Redirect = "redirect",
    Success = "success",
    PushToDevice = "push-to-device",
}

export const enum Params {
    OrgId = ":orgId",
    SubmissionId = ":submissionId",
    LensFolderId = ":lensFolderId",
    LensId = ":lensId",
    RouteTo = ":routeTo",
    ApiId = ":apiId",
    GroupId = ":groupId",
    UnlockableId = ":unlockableId",
    Reference = ":reference",
    LensSourceType = ":lensSourceType",
    AppId = ":appId",
    InsightsTab = ":insightsTab",
    MonetizationTab = ":monetizationTab",
}

type RouteTree = string | { [key: string]: RouteTree; index: string };

export const relativeRoutes = {
    index: "/",
    // Lens Studio Contract - Do Not Modify
    createOrgLensFolder: `create-org-lens-folder/${Params.RouteTo}/${Params.SubmissionId}?`,

    lensStudioRedirect: `${RouteEntities.LensStudio}/${RouteActions.Redirect}`,

    submit: {
        // Lens Studio Contract - Do Not Modify
        index: `${RouteEntities.Submit}/${Params.SubmissionId}`,
        flowRedirector: RouteEntities.FlowRedirector,
        lensFolders: {
            index: RouteEntities.LensFolders,
            select: RouteActions.Select,
            create: RouteActions.Create,
        },
        lenses: {
            index: RouteEntities.Lenses,
            select: RouteActions.Select,
            create: RouteActions.Create,
            update: `${RouteActions.Update}/${Params.LensId}`,
            success: `${RouteActions.Success}/${Params.LensId}`,
        },
        accountsSelect: `${RouteEntities.Accounts}/${RouteActions.Select}`,
    },

    apis: {
        index: RouteEntities.Apis,
        create: RouteActions.Create,
        api: Params.ApiId,
    },

    lenses: RouteEntities.Lenses,
    lens: `${RouteEntities.Lens}/${Params.LensId}`,
    insights: {
        index: RouteEntities.Insights,
        insightsTab: Params.InsightsTab,
    },
    monetization: {
        index: RouteEntities.Monetization,
        monetizationTab: Params.MonetizationTab,
    },
    welcome: {
        index: RouteEntities.Welcome,
        organization: Params.OrgId,
    },
    lensFolders: RouteEntities.LensFolders,
    home: RouteEntities.Home,
    communityAudience: RouteEntities.CommunityAudience,

    organization: {
        index: Params.OrgId,
        lenses: RouteEntities.Lenses,
        insights: {
            index: RouteEntities.Insights,
            insightsTab: Params.InsightsTab,
        },
        monetization: {
            index: RouteEntities.Monetization,
            monetizationTab: Params.MonetizationTab,
        },
        assets: {
            index: RouteEntities.Assets,
            create: RouteActions.Create,
        },
        lensFolders: {
            index: RouteEntities.LensFolders,
            lensFolder: Params.LensFolderId,
            insights: {
                index: `${Params.LensFolderId}/${RouteEntities.Insights}`,
                insightsTab: Params.InsightsTab,
            },
            monetization: {
                index: `${Params.LensFolderId}/${RouteEntities.Monetization}`,
                monetizationTab: Params.MonetizationTab,
            },
            lenses: `${Params.LensFolderId}/${RouteEntities.Lenses}`,
            lens: `${Params.LensFolderId}/${RouteEntities.Lenses}/${Params.LensId}`,
        },
        webLenses: {
            index: RouteEntities.WebLenses,
            submit: RouteEntities.Submit,
            update: RouteActions.Update,
            pushToDevice: RouteActions.PushToDevice,
        },
        cameraKit: {
            index: RouteEntities.CameraKit,
            // Camera Kit
            lensScheduler: {
                index: RouteEntities.LensScheduler,
                groups: {
                    index: RouteEntities.Groups,
                    group: Params.GroupId,
                    lens: `${Params.GroupId}/${RouteEntities.Lenses}/${Params.LensId}/${RouteEntities.References}/${Params.Reference}/${RouteEntities.Sources}/${Params.LensSourceType}`,
                },
                lenses: {
                    index: RouteEntities.Lenses,
                    lens: `${Params.LensId}/${RouteEntities.References}/${Params.Reference}/${RouteEntities.Sources}/${Params.LensSourceType}`,
                },
                sources: RouteEntities.Sources,
            },

            apps: {
                index: RouteEntities.Apps,
                app: Params.AppId,
                create: RouteActions.Create,
            },
        },
        hostedLenses: RouteEntities.HostedLenses,
        remoteAssets: RouteEntities.RemoteAssets,
    },

    cameraKit: {
        index: RouteEntities.CameraKit,
        // top level routes with redirects
        lensScheduler: RouteEntities.LensScheduler,
        eula: RouteEntities.Eula,
        apps: RouteEntities.Apps,
    },

    assets: {
        index: RouteEntities.Assets,
        create: RouteActions.Create,
    },

    hostedLenses: RouteEntities.HostedLenses,

    accessDenied: "access-denied",
    noRoleAssigned: "no-role-assigned",
} as const satisfies RouteTree;
